import React, { useEffect, useMemo, useState } from 'react';
import { Sidebar } from 'semantic-ui-react';
import { Navigate, Outlet, useLocation, useSearchParams } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import { canIUse, Features } from './helpers/PermissionUtility';
import { analyticsTrack } from './helpers/AnalyticsUtility';
import SuccessLauncher from './successlauncher/SuccessLauncher';
import { useAppSelector } from './hooks/ReduxHooks';
import { appContext } from './AppContext';
import { useUserPersistedState } from './persistedstate/users/useUserPersistedState';
import MainMenu from './MainMenu/MainMenu';
import { SubscriptionCodes } from './helpers/constants';
import moment from 'moment';
import BannerMessage from './components/banner/BannerMessage';

export default function Main() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const clientLocked = useAppSelector<boolean>((state) => state.shared.clientLocked);
  const firstName = useAppSelector<string>((state) => state.profile.user.firstName);
  const showTrialExpiredLandingPage = useAppSelector<boolean>(
    (state) => state.profile.client?.subscription === SubscriptionCodes.Basic && !state.profile.isInTrial && state.profile.client?.showTrialExpiredLandingPage
  );
  const trialEndDate = useAppSelector<string>((state) => state.profile.client?.trialEndDate);
  const [successLauncherOpen, setSuccessLauncherOpen] = useState(false);
  const [successLauncherVisible, setSuccessLauncherVisible] = useState(false);
  const [firstTemplateStarted, setFirstTemplateStarted] = useUserPersistedState('firstTemplateStarted');
  const [firstTemplatePublished, setFirstTemplatePublished] = useUserPersistedState('firstTemplatePublished');
  const initialLogin = useMemo(() => (searchParams.get('initialLogin') || searchParams.get('initialLogin')) === 'true', [searchParams]);
  const showOnboardingSurvey = initialLogin && !appContext.onboardingSurveyCompleted && canIUse(Features.ChooseJourney);
  const daysSinceTrialExpired = moment.duration(moment(moment.utc()).diff(trialEndDate)).asDays();

  const openSuccessLauncher = () => {
    analyticsTrack('Help Question Mark - Selected');
    setSuccessLauncherOpen(true);
  };

  // let user press escape to get out of onboarding flow with disabled navigation
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && (!firstTemplateStarted || !firstTemplatePublished)) {
        setFirstTemplateStarted(true);
        setFirstTemplatePublished(true);
      }
    };

    document.body.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  if (clientLocked) {
    return <div className="client-locked">Your account is currently undergoing maintenance.</div>;
  }

  // if they made it here and it's the first time they're visiting the webapp
  // take them to the survey page then the preview page.
  //
  // we're doing this check here because we need all of the routes to load before we can
  // successfully redirect to the preview route
  if (showOnboardingSurvey) {
    return <Navigate to="/survey" />;
  }

  if (showTrialExpiredLandingPage && daysSinceTrialExpired < 14) {
    return <Navigate to="/expiredtrial" />;
  }

  return (
    <ErrorBoundary>
      <BannerMessage />
      <MainMenu onSuccessLauncherClick={() => openSuccessLauncher()} />
      <div id="goformz-app-body">
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </div>
      <Sidebar
        animation="overlay"
        onHide={() => setSuccessLauncherOpen(false)}
        onHidden={() => setSuccessLauncherVisible(false)}
        onVisible={() => setSuccessLauncherVisible(true)}
        visible={successLauncherOpen}
        className="success-launcher-sidebar"
        direction="right"
      >
        {successLauncherVisible ? <SuccessLauncher onClose={() => setSuccessLauncherOpen(false)} firstName={firstName} location={location} /> : null}
      </Sidebar>
    </ErrorBoundary>
  );
}
